import { useEffect, useRef, useState, useCallback } from "react";
import { io, type Socket } from "socket.io-client";
import { AdminLayout } from "@/components/layout/AdminLayout";
import { useAuth } from "@/hooks/use-auth";
import { Redirect } from "wouter";

/**
 * Admin remote-support console (admin-only). Search a user, start a consented live session,
 * watch their streamed screen, and drive their app with one-tap buttons (no typing). Relayed
 * over the existing Socket.IO server; the user must accept the on-device consent prompt first.
 * Inert unless the `support_enabled` platform setting is on.
 */
type FoundUser = { id: number; name: string | null; email: string | null; role: string };

// One-tap navigation targets (uses the already-shipped `navigate` command).
const NAV_BUTTONS: { label: string; path: string }[] = [
  { label: "Home", path: "/(tabs)" },
  { label: "My Orders", path: "/my-orders" },
  { label: "Shop Portal", path: "/order-store-portal" },
  { label: "Driver Jobs", path: "/driver-jobs" },
  { label: "Account", path: "/account" },
  { label: "Help", path: "/help" },
];

export default function AdminRemoteSupport() {
  const { user } = useAuth();
  const socketRef = useRef<Socket | null>(null);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<FoundUser[]>([]);
  const [selected, setSelected] = useState<FoundUser | null>(null);
  const [sessionUser, setSessionUser] = useState<number | null>(null);
  const [consent, setConsent] = useState<"pending" | "accepted" | "declined" | null>(null);
  const [frame, setFrame] = useState<string | null>(null);
  const [frameAt, setFrameAt] = useState<number | null>(null);
  const [targets, setTargets] = useState<{ id: string; x: number; y: number; w: number; h: number }[]>([]);
  const [mode, setMode] = useState<"control" | "guide">("control"); // control = tap their buttons, guide = draw
  const [hoverId, setHoverId] = useState<string | null>(null);
  const [tapFx, setTapFx] = useState<{ x: number; y: number; k: number } | null>(null);
  const [log, setLog] = useState<string[]>([]);
  const addLog = useCallback((m: string) => setLog((l) => [`${new Date().toLocaleTimeString()}  ${m}`, ...l].slice(0, 60)), []);

  const isStaff = user?.role === "admin";

  // socket
  useEffect(() => {
    if (!isStaff) return;
    const token = localStorage.getItem("ayrshire_token");
    if (!token) { addLog("No admin token — please log in."); return; }
    const sock = io({ auth: { token }, transports: ["websocket", "polling"] });
    socketRef.current = sock;
    sock.on("connect", () => addLog("Connected to support relay."));
    sock.on("support:error", (d: { error?: string }) => addLog(`Error: ${d?.error ?? "unknown"}`));
    sock.on("support:requested", (d: { targetUserId?: number }) => { setSessionUser(d?.targetUserId ?? null); setConsent("pending"); addLog(`Request sent — waiting for the user to tap Allow…`); });
    sock.on("support:consent", (d: { accepted?: boolean }) => { setConsent(d?.accepted ? "accepted" : "declined"); addLog(d?.accepted ? "User ACCEPTED — streaming." : "User DECLINED."); });
    sock.on("support:frame", (d: { image?: string; ts?: number }) => { if (d?.image) { setFrame(d.image); setFrameAt(d.ts ?? Date.now()); } });
    sock.on("support:targets", (d: { targets?: { id: string; x: number; y: number; w: number; h: number }[] }) => { if (Array.isArray(d?.targets)) setTargets(d.targets); });
    sock.on("support:state", (d: { state?: { w?: number; h?: number } }) => { const w = Number(d?.state?.w), h = Number(d?.state?.h); if (w > 0 && h > 0) theirSize.current = { w, h }; });
    sock.on("support:ended", () => { addLog("Session ended."); setConsent(null); setFrame(null); setTargets([]); });
    return () => { try { sock.off(); sock.disconnect(); } catch { /* noop */ } socketRef.current = null; };
  }, [isStaff, addLog]);

  // debounced user search
  useEffect(() => {
    if (!isStaff) return;
    const q = query.trim();
    if (q.length < 2) { setResults([]); return; }
    const token = localStorage.getItem("ayrshire_token");
    const t = setTimeout(() => {
      void fetch(`/api/admin/users/search?q=${encodeURIComponent(q)}`, { headers: { Authorization: `Bearer ${token ?? ""}` } })
        .then((r) => (r.ok ? r.json() : { users: [] }))
        .then((d: { users?: FoundUser[] }) => setResults(d.users ?? []))
        .catch(() => setResults([]));
    }, 250);
    return () => clearTimeout(t);
  }, [query, isStaff]);

  if (!isStaff) return <Redirect to="/" />;

  const startWith = (u: FoundUser) => {
    setSelected(u); setResults([]); setQuery(""); setFrame(null); setConsent(null);
    socketRef.current?.emit("support:request", { targetUserId: u.id });
    addLog(`Starting session with ${u.name ?? u.email ?? u.id} (#${u.id})`);
  };
  const end = () => { if (sessionUser) socketRef.current?.emit("support:end", { targetUserId: sessionUser }); setConsent(null); setFrame(null); setSelected(null); setSessionUser(null); setTargets([]); };
  const cmd = (action: string, payload?: unknown) => { if (sessionUser && consent === "accepted") { socketRef.current?.emit("support:command", { targetUserId: sessionUser, action, payload }); addLog(`Sent: ${action}${payload ? " " + JSON.stringify(payload) : ""}`); } };

  const live = consent === "accepted";

  // ── Draw-to-guide: draw on the screenshot; strokes (normalised 0..1) overlay on their phone ──
  type P = { x: number; y: number };
  const imgWrapRef = useRef<HTMLDivElement>(null);
  const drawingRef = useRef(false);
  const drawClearTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [strokes, setStrokes] = useState<P[][]>([]);
  const [cur, setCur] = useState<P[]>([]);
  const normPt = (e: React.PointerEvent): P | null => {
    const el = imgWrapRef.current; if (!el) return null;
    const r = el.getBoundingClientRect();
    return { x: Math.min(1, Math.max(0, (e.clientX - r.left) / r.width)), y: Math.min(1, Math.max(0, (e.clientY - r.top) / r.height)) };
  };
  const downPt = useRef<P | null>(null);
  const lastPt = useRef<P | null>(null);
  const downRef = useRef(false);
  const movedRef = useRef(false);
  const theirSize = useRef({ w: 390, h: 844 });
  const sendTap = (p: P) => {
    cmd("tap", { x: p.x, y: p.y });
    setTapFx({ x: p.x, y: p.y, k: Math.round(p.x * 1e4) + Math.round(p.y * 1e4) });
    setTimeout(() => setTapFx(null), 450);
  };
  const onDown = (e: React.PointerEvent) => {
    if (!live || !frame) return;
    const p = normPt(e); if (!p) return;
    downPt.current = p; lastPt.current = p; downRef.current = true; movedRef.current = false;
    if (mode === "guide") {
      if (drawClearTimer.current) { clearTimeout(drawClearTimer.current); drawClearTimer.current = null; } // keep drawing alive while in use
      drawingRef.current = true; setCur([p]);
    }
  };
  const onMove = (e: React.PointerEvent) => {
    const p = normPt(e); if (!p) return;
    if (mode === "guide") { if (drawingRef.current) setCur((c) => [...c, p]); return; }
    // CONTROL: drag = scroll their screen by the matching device-pixel delta.
    if (!downRef.current || !lastPt.current) return;
    const dyPix = (lastPt.current.y - p.y) * theirSize.current.h;
    const dxPix = (lastPt.current.x - p.x) * theirSize.current.w;
    if (Math.abs(dyPix) > 1.5 || Math.abs(dxPix) > 1.5) { movedRef.current = true; cmd("scroll", { dx: dxPix, dy: dyPix }); }
    lastPt.current = p;
  };
  const onFinish = (fromUp: boolean) => {
    // CONTROL mode: a clean click (no drag) = tap; a drag = scroll (already sent during move).
    if (mode === "control") {
      if (fromUp && !movedRef.current && downPt.current) sendTap(downPt.current);
      downPt.current = null; lastPt.current = null; downRef.current = false;
      return;
    }
    // GUIDE mode: commit the drawn stroke.
    if (!drawingRef.current) return; drawingRef.current = false;
    setCur((c) => {
      if (c.length > 1) {
        const all = [...strokes, c]; setStrokes(all); cmd("annotate", { strokes: all });
        // Match the phone: fade our own guide strokes 3s after the last one is drawn.
        if (drawClearTimer.current) clearTimeout(drawClearTimer.current);
        drawClearTimer.current = setTimeout(() => { setStrokes([]); setCur([]); }, 3000);
      }
      return [];
    });
  };
  const clearDraw = () => {
    if (drawClearTimer.current) { clearTimeout(drawClearTimer.current); drawClearTimer.current = null; }
    setStrokes([]); setCur([]); cmd("clearAnnotations");
  };

  return (
    <AdminLayout current="/admin/remote-support">
      <div className="max-w-5xl">
        <h1 className="text-xl font-bold text-slate-800 mb-1">Remote Support</h1>
        <p className="text-sm text-slate-500 mb-4">Search a user, start a session, and help them with one-tap controls. They must tap “Allow” on their phone first. (Note: Apple/Google don't allow tapping their actual screen remotely — use the buttons to drive their app.)</p>

        {/* Search / selected user */}
        {!sessionUser ? (
          <div className="mb-4 max-w-md relative">
            <label className="block text-xs font-semibold text-slate-500 mb-1">Find a user (name or email)</label>
            <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Start typing a name or email…" autoFocus
                   className="border border-slate-300 rounded-lg px-3 py-2 text-sm w-full" />
            {results.length > 0 && (
              <div className="absolute z-10 mt-1 w-full bg-white border border-slate-200 rounded-xl shadow-lg overflow-hidden">
                {results.map((u) => (
                  <button key={u.id} onClick={() => startWith(u)} className="w-full text-left px-3 py-2 hover:bg-slate-50 border-b border-slate-50 last:border-0">
                    <span className="font-medium text-slate-800">{u.name || "(no name)"}</span>
                    <span className="text-xs text-slate-400"> · {u.email} · #{u.id} · {u.role}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        ) : (
          <div className="flex flex-wrap items-center gap-3 mb-4">
            <span className="text-sm text-slate-700">Session with <strong>{selected?.name || selected?.email || `#${sessionUser}`}</strong> (#{sessionUser})</span>
            <span className={`text-xs font-semibold px-2 py-1 rounded-full ${consent === "accepted" ? "bg-emerald-100 text-emerald-700" : consent === "pending" ? "bg-amber-100 text-amber-700" : "bg-red-100 text-red-700"}`}>{consent ?? "…"}</span>
            <button onClick={end} className="bg-red-600 text-white font-semibold rounded-lg px-4 py-1.5 text-sm">End session</button>
          </div>
        )}

        {/* Control / Guide mode toggle */}
        {live && frame && (
          <div className="flex items-center gap-2 mb-2">
            <div className="inline-flex rounded-lg border border-slate-200 overflow-hidden text-xs font-semibold">
              <button onClick={() => setMode("control")} className={`px-3 py-1.5 ${mode === "control" ? "bg-[#1e3a5f] text-white" : "bg-white text-slate-600"}`}>👆 Control (tap their buttons)</button>
              <button onClick={() => setMode("guide")} className={`px-3 py-1.5 ${mode === "guide" ? "bg-[#ff2d55] text-white" : "bg-white text-slate-600"}`}>✏️ Guide (draw)</button>
            </div>
            <span className="text-[11px] text-slate-400">{mode === "control" ? "Click a control to press it · drag to scroll their screen." : "Draw on the screen to point them to it (fades after 3s)."}</span>
          </div>
        )}
        <div className="grid md:grid-cols-[1fr,300px] gap-4">
          <div className="bg-slate-900 rounded-2xl overflow-hidden flex items-center justify-center min-h-[480px]">
            {frame ? (
              <div ref={imgWrapRef} className="relative inline-block" style={{ touchAction: "none", cursor: live ? (mode === "control" ? "pointer" : "crosshair") : "default" }}
                   onPointerDown={onDown} onPointerMove={onMove} onPointerUp={() => onFinish(true)} onPointerLeave={() => onFinish(false)}>
                <img src={frame} alt="user screen" className="max-h-[70vh] w-auto block select-none" draggable={false} />
                {/* Tappable control zones (Control mode only) — outlines over the real buttons. */}
                {mode === "control" && live && targets.map((t) => (
                  <div key={t.id}
                       onMouseEnter={() => setHoverId(t.id)} onMouseLeave={() => setHoverId((h) => (h === t.id ? null : h))}
                       title={t.id}
                       className="absolute pointer-events-none rounded-[3px]"
                       style={{ left: `${t.x * 100}%`, top: `${t.y * 100}%`, width: `${t.w * 100}%`, height: `${t.h * 100}%`,
                                border: `1.5px solid ${hoverId === t.id ? "#22d3ee" : "rgba(34,211,238,0.5)"}`,
                                background: hoverId === t.id ? "rgba(34,211,238,0.18)" : "rgba(34,211,238,0.06)" }} />
                ))}
                {/* Tap feedback ripple */}
                {tapFx && (
                  <span key={tapFx.k} className="absolute pointer-events-none rounded-full"
                        style={{ left: `${tapFx.x * 100}%`, top: `${tapFx.y * 100}%`, width: 26, height: 26, marginLeft: -13, marginTop: -13,
                                 border: "2px solid #22d3ee", background: "rgba(34,211,238,0.25)" }} />
                )}
                {/* Guide drawing overlay */}
                <svg viewBox="0 0 1 1" preserveAspectRatio="none" className="absolute inset-0 w-full h-full pointer-events-none">
                  {[...strokes, cur].map((s, i) => s.length > 1 ? (
                    <polyline key={i} points={s.map((p) => `${p.x},${p.y}`).join(" ")} fill="none" stroke="#ff2d55" strokeWidth={2} vectorEffect="non-scaling-stroke" strokeLinecap="round" strokeLinejoin="round" />
                  ) : null)}
                </svg>
                {live && <span className="absolute top-1 left-1 text-[10px] bg-black/50 text-white px-1.5 py-0.5 rounded">{mode === "control" ? `👆 tap to control${targets.length ? ` · ${targets.length} controls` : ""}` : "✏️ draw to guide them"}</span>}
              </div>
            ) : <p className="text-slate-400 text-sm p-8 text-center">{sessionUser ? (consent === "pending" ? "Waiting for the user to tap Allow on their phone…" : consent === "declined" ? "User declined the session." : "No screen yet.") : "Search for a user above to start."}</p>}
          </div>
          <div className="space-y-3">
            <div className="bg-white border border-slate-200 rounded-xl p-3">
              <h3 className="font-semibold text-sm text-slate-700 mb-2">Take them to…</h3>
              <div className="grid grid-cols-2 gap-2">
                {NAV_BUTTONS.map((b) => (
                  <button key={b.path} onClick={() => cmd("navigate", { path: b.path })} disabled={!live}
                          className="bg-[#1e3a5f] text-white text-xs font-semibold rounded-lg px-3 py-2 disabled:opacity-30">{b.label}</button>
                ))}
              </div>
            </div>
            <div className="bg-white border border-slate-200 rounded-xl p-3">
              <h3 className="font-semibold text-sm text-slate-700 mb-2">Actions</h3>
              <div className="flex flex-wrap gap-2">
                <button onClick={() => cmd("openPrinterSetup")} disabled={!live} className="bg-[#1e3a5f] text-white text-xs font-semibold rounded-lg px-3 py-1.5 disabled:opacity-30">Open Printer Setup</button>
                <button onClick={() => cmd("refresh")} disabled={!live} className="bg-slate-100 text-slate-700 text-xs font-semibold rounded-lg px-3 py-1.5 disabled:opacity-30">Refresh view</button>
                <button onClick={() => { const m = prompt("Message to show on their phone:"); if (m) cmd("showMessage", { title: "Ayrshire Hub Support", body: m }); }} disabled={!live} className="bg-slate-100 text-slate-700 text-xs font-semibold rounded-lg px-3 py-1.5 disabled:opacity-30">Send message…</button>
                <button onClick={clearDraw} disabled={!live} className="bg-slate-100 text-slate-700 text-xs font-semibold rounded-lg px-3 py-1.5 disabled:opacity-30">Clear drawing</button>
                <button onClick={() => { if (confirm("Reload the user's app?")) cmd("reload"); }} disabled={!live} className="bg-slate-100 text-slate-700 text-xs font-semibold rounded-lg px-3 py-1.5 disabled:opacity-30">Reload app</button>
              </div>
            </div>
            <div className="bg-white border border-slate-200 rounded-xl p-3">
              <h3 className="font-semibold text-sm text-slate-700 mb-2">Activity{frameAt ? ` · ${new Date(frameAt).toLocaleTimeString()}` : ""}</h3>
              <div className="text-[11px] font-mono text-slate-500 space-y-0.5 max-h-64 overflow-y-auto">
                {log.map((l, i) => <div key={i}>{l}</div>)}
              </div>
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
